/* global axios */
import ApiClient from './ApiClient';

// A settings read that never answers would keep the settings page loading,
// and a save overtaken by a settings event waits for that read (see
// settingsRequest in the store). Reads are safe to give up on and retry.
// Writes are not timed out: one the server applied after the client gave up
// would be reported as not saved.
export const SETTINGS_READ_TIMEOUT = 30000;

// Account scoped AI suggestion endpoints. See ai-docs/ai-suggestions.md.
class AiSuggestionsAPI extends ApiClient {
  constructor() {
    super('ai_suggestions', { accountScoped: true });
  }

  getConfig() {
    return axios.get(`${this.url}/config`, {
      timeout: SETTINGS_READ_TIMEOUT,
    });
  }

  updateConfig(data) {
    return axios.patch(`${this.url}/config`, data);
  }

  getPromptVersions() {
    return axios.get(`${this.url}/prompt_versions`);
  }

  restorePromptVersion(id) {
    return axios.post(`${this.url}/prompt_versions/${id}/restore`);
  }

  getAgentSetting() {
    return axios.get(`${this.url}/agent_setting`, {
      timeout: SETTINGS_READ_TIMEOUT,
    });
  }

  updateAgentSetting({ active }) {
    return axios.patch(`${this.url}/agent_setting`, { active });
  }

  getAgents() {
    return axios.get(`${this.url}/agents`);
  }

  updateAgent(userId, { allowed }) {
    return axios.patch(`${this.url}/agents/${userId}`, { allowed });
  }

  getFeedback({
    outcome,
    q,
    tag,
    risk_level,
    edit_size,
    since,
    until,
    page,
    sort,
  } = {}) {
    return axios.get(`${this.url}/feedback`, {
      params: {
        outcome,
        q,
        tag,
        risk_level,
        edit_size,
        since,
        until,
        page,
        sort,
      },
    });
  }

  // The Analytics tab: speed, usage and outcomes in a period (at most 90
  // days), bucketed per hour or day in the viewer's time zone.
  getAnalytics(params = {}) {
    return axios.get(`${this.url}/analytics`, { params });
  }

  // Everything stored for one suggestion. Administrators only.
  getDetails(id) {
    return axios.get(`${this.url}/${id}/details`);
  }

  getFeedbackSummary({ since, until } = {}) {
    return axios.get(`${this.url}/feedback/summary`, {
      params: { since, until },
    });
  }

  preview({ conversationId, promptBody, model, mode }) {
    return axios.post(`${this.url}/preview`, {
      conversation_id: conversationId,
      prompt_body: promptBody,
      model,
      mode,
    });
  }
}

export default new AiSuggestionsAPI();
